import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Phone, User, Home, ExternalLink, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const TYPE_ICON = {
  unread_message: Mail,
  pending_showing: Calendar,
  callback: Phone,
  uncontacted_lead: User,
  new_match: Home,
  new_seller_lead: Home,
};

function timeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function greetingSubline(totalCount) {
  const n = Number(totalCount) || 0;
  if (n <= 0) return 'Nothing needs you right now';
  if (n === 1) return '1 person is waiting on you';
  return `${n} people are waiting on you`;
}

export default function Dashboard() {
  const { agent } = useAuth();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [matches, setMatches] = useState({ recent: [], this_week: 0, recent_total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [confirmingId, setConfirmingId] = useState('');
  const [flashId, setFlashId] = useState('');

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    setError(false);
    try {
      const needsParams = opts.showAll ? { params: { limit: 50 } } : undefined;
      const [needsRes, dashRes] = await Promise.all([
        api.get('/dashboard/needs-attention', needsParams),
        api.get('/dashboard').catch(() => ({ data: null })),
      ]);
      setPayload(needsRes.data || { items: [], total_count: 0, summary: {} });
      const d = dashRes.data;
      setMatches({
        recent: d?.recent_buyer_matches || [],
        this_week: d?.buyer_matches?.this_week || 0,
        recent_total: d?.buyer_matches?.recent_total || 0,
      });
    } catch (_) {
      setError(true);
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAction = async (item) => {
    const kind = item?.action?.kind;
    const targetId = item?.action?.target_id;
    if (!kind) return;

    if (kind === 'confirm_showing') {
      if (!targetId || confirmingId) return;
      setConfirmingId(targetId);
      try {
        await api.patch(`/appointments/${targetId}`, {
          status: 'confirmation_sent',
          agent_confirmed: true,
        });
        setFlashId(targetId);
        await load();
        setTimeout(() => setFlashId(''), 2500);
      } catch (err) {
        const data = err?.response?.data;
        const phone = data?.buyer_phone ? ` Their number is ${data.buyer_phone}.` : '';
        window.alert((data?.error || 'Failed to send confirmation') + phone);
      } finally {
        setConfirmingId('');
      }
      return;
    }

    if (kind === 'open_callback') {
      navigate(targetId ? `/appointments?id=${targetId}` : '/appointments');
      return;
    }
    if (kind === 'open_lead') {
      navigate(targetId ? `/leads?id=${targetId}` : '/leads');
      return;
    }
    if (kind === 'open_match') {
      const clientId = item.person_id;
      navigate(clientId ? `/clients?client=${clientId}` : '/clients');
      return;
    }
    if (kind === 'open_messages' || kind === 'open_client') {
      navigate(targetId ? `/clients?open=${targetId}` : '/clients');
    }
  };

  const items = payload?.items || [];
  const totalCount = payload?.total_count ?? 0;
  const summary = payload?.summary || {};
  const moreCount = Math.max(0, totalCount - items.length);
  const empty = !loading && !error && totalCount === 0;

  return (
    <div>
      <div className="page-header">
        <h2>{timeOfDay()}, {agent?.first_name}</h2>
        <p>{loading ? 'Loading your list…' : greetingSubline(totalCount)}</p>
      </div>

      <section className="card needs-panel" aria-label="Needs you today">
        <div className="card-header">
          <span className="card-title">Needs you today</span>
        </div>

        {loading && (
          <div className="needs-list">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="needs-row needs-row--skeleton" aria-hidden="true">
                <div className="needs-skel needs-skel--icon" />
                <div className="needs-skel-body">
                  <div className="needs-skel needs-skel--line" />
                  <div className="needs-skel needs-skel--line short" />
                </div>
                <div className="needs-skel needs-skel--btn" />
              </div>
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="needs-state">
            <p>Couldn&apos;t load your list. Retry</p>
            <button type="button" className="btn btn-primary" onClick={load}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && empty && (
          <div className="needs-state">
            <p>Nothing needs you right now.</p>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className="needs-list">
              {items.map((item) => {
                const Icon = TYPE_ICON[item.type] || Calendar;
                const isConfirming = confirmingId === item.action?.target_id;
                const justConfirmed = flashId === item.action?.target_id;
                return (
                  <div key={`${item.type}-${item.action?.target_id || item.person_id}`} className="needs-row">
                    <div className="needs-row__icon" aria-hidden="true">
                      <Icon size={16} />
                    </div>
                    <div className="needs-row__body">
                      <div className="needs-row__headline">{item.headline}</div>
                      <div className="needs-row__meta">
                        {String(item.detail || '')
                          .split(' · ')
                          .filter((part) => part && !/\b(seed|demo)\b/i.test(part))
                          .join(' · ')}
                        {item.age_label ? ` · ${item.age_label}` : ''}
                      </div>
                      {justConfirmed && (
                        <div className="needs-row__flash">Confirmation sent</div>
                      )}
                    </div>
                    {item.action?.label && (
                      <button
                        type="button"
                        className="btn btn-primary needs-row__action"
                        disabled={isConfirming}
                        onClick={() => handleAction(item)}
                      >
                        {isConfirming ? 'Sending…' : item.action.label}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {moreCount > 0 && (
              <button
                type="button"
                className="needs-more needs-more--link"
                onClick={() => load({ showAll: true })}
              >
                and {moreCount} more — show all
              </button>
            )}
          </>
        )}
      </section>

      {!loading && !error && (
        <div className="needs-metrics">
          <button
            type="button"
            className="stat-card stat-card--link needs-metric-card"
            onClick={() => navigate('/leads')}
          >
            <div className="stat-label">Active leads</div>
            <div className="stat-value">{summary.active_leads ?? 0}</div>
          </button>
          <button
            type="button"
            className="stat-card stat-card--link needs-metric-card"
            onClick={() => navigate('/chat')}
          >
            <div className="stat-label">Conversations this week</div>
            <div className="stat-value">{summary.conversations_this_week ?? 0}</div>
          </button>
          <button
            type="button"
            className="stat-card stat-card--link needs-metric-card"
            onClick={() => navigate('/appointments')}
          >
            <div className="stat-label">Upcoming showings</div>
            <div className="stat-value">{summary.upcoming_showings ?? 0}</div>
          </button>
        </div>
      )}

      {!loading && !error && empty && agent?.slug && (
        <div className="card needs-share">
          <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--text-secondary)' }}>
            Share your public chat link so buyers can reach you anytime.
          </p>
          <a
            href={`/chat/${agent.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}
          >
            <ExternalLink size={14} /> Preview public chat
          </a>
        </div>
      )}

      {!loading && (matches.recent.length > 0 || matches.recent_total > 0) && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header">
            <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Home size={16} /> Buyer listing matches
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {matches.this_week || 0} this week · {matches.recent_total || 0} active (14d)
            </span>
          </div>
          {matches.recent.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {matches.recent.map((m) => (
                <div
                  key={m.id}
                  style={{
                    padding: '12px 14px',
                    borderRadius: 10,
                    border: '1px solid var(--border)',
                    background: 'var(--accent-dim)',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                    {m.first_name} {m.last_name}
                    <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>
                      {' '}→ {m.address_line1}, {m.city}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                    ${Number(m.list_price).toLocaleString()}
                    {m.bedrooms != null ? ` · ${m.bedrooms} bed` : ''}
                    {m.score != null ? ` · score ${Math.round(Number(m.score))}` : ''}
                    {' · '}{new Date(m.created_at).toLocaleDateString()}
                  </div>
                  {m.reason && (
                    <div style={{ fontSize: 12, color: 'var(--accent-bright)', marginTop: 4 }}>{m.reason}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
              Matches are created when saved-search buyers chat or refresh criteria — they'll show up here, and buyers see them as "New for you" in their own portal.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
